/**
 * `LedgerBinding` — the per-sheet object the row-keyed stores receive to bump
 * `data:` nodes (row-identity ledger, slice L2). Split out of `ledger-keys.ts`
 * because it is the only ledger piece that depends on a live row's address
 * (`RowAddress`, `../rows/row-address.ts`): keeping it here means
 * `../rows/variants/source-row.ts` can import `LedgerKeys` (its range bump)
 * without dragging `row-address` — and the variant classes it pulls — into a
 * module-evaluation cycle (`source-row → ledger-keys → row-address →
 * computed-data-source → source-row` would TDZ-crash on `extends SourceRow`).
 * Nothing in that variant chain imports this module, so evaluating it triggers
 * a clean linear eval of `row-address`.
 */

import type { Option } from "../core/option.ts";

import { AddressOf, type Address } from "../ids/address-of.ts";
import { RowAddress } from "../rows/row-address.ts";
import { type CellLedgerBinding, cellLedgerBump, type CellLedgerBump, type LedgerBumper } from "../stores/ledger-keys.ts";
import type { RowLocalId } from "../ids/row-local-id.ts";
import type { ModelRow } from "../rows/model-row.ts";

/**
 * The shared `LedgerBumper`, a `RowLocalId → Address` resolver, and a late-bound
 * row lookup. Before `attachRowLookup`, resolution uses only what a `RowLocalId`
 * affords (`AddressOf.forLocalId`); after, it delegates to the live row via
 * `AddressOf` and only falls back on a lookup miss.
 *
 * ## Two resolvers — the write path is total, the read path is honest
 *
 * `resolveAddress` (write / bump) is **total**: a bump must always have a
 * target, so a placement-only row falls back to the coarse
 * `data:local:<serial>`. `resolveOutputAddress` (read / compare) returns
 * `Option`: for a placement-only row — one `AddressOf` classifies as owning NO
 * data node (`system(...)`, `pending`, `template`, alias containers) — it
 * returns `null`, because comparing against the write path's fallback node
 * would compare against a node **nothing ever writes to**: a frozen version 0
 * that reads "output unchanged" forever. That phantom-node compare is the root
 * of the 2026-08-06 header zero-height break and the 2026-08-07 create-does-
 * not-paint demote — LR6's own contract ("placement-only never skips",
 * `ledger-render-skip.ts`) requires the probe to say `null` here.
 *
 * No memoization in L2 — correctness first. Each resolver runs once per cell
 * write / per bind-time probe; if it shows up hot in a profile, memoize per
 * `RowLocalId` with invalidation on the LR7 address-swap event (follow-up).
 */
export interface LedgerBinding extends CellLedgerBinding {
  readonly bumper: LedgerBumper;
  /** The row's data node for the WRITE path — total (a bump always has a target). */
  resolveAddress(id: RowLocalId): Address;
  /**
   * The row's OUTPUT data node for the READ/compare path — `null` when the live
   * row classifies placement-only (no data node ⇒ nothing to compare ⇒ never
   * skip). Pre-attachment, or on a lookup miss, falls back to the same coarse
   * per-id address as the write path (conservative: a comparable node).
   */
  resolveOutputAddress(id: RowLocalId): Option<Address>;
  attachRowLookup(lookup: (id: RowLocalId) => Option<ModelRow>): void;
}

export namespace LedgerBinding {
  /** Build a per-sheet binding over the shared `bumper`. */
  export function forSheet(bumper: LedgerBumper): LedgerBinding {
    let lookup: Option<(id: RowLocalId) => Option<ModelRow>> = null;
    // The read-path resolver (see the interface doc): the live row's
    // classification when available — `null` for placement-only — and the
    // coarse per-id address pre-attachment / on a lookup miss.
    const resolveOutputAddress = (id: RowLocalId): Option<Address> => {
      if (lookup != null) {
        const row = lookup(id);
        if (row != null) {
          const result = RowAddress.of(row);
          return result.kind === "data" ? result.address : null;
        }
      }
      return AddressOf.forLocalId(id);
    };
    return {
      bumper,
      resolveAddress(id: RowLocalId): Address {
        // The write path stays total: a placement-only classification (read
        // path `null`) falls back to the coarse address so a bump always has
        // a target. Behavior-identical to the pre-split resolver.
        return resolveOutputAddress(id) ?? AddressOf.forLocalId(id);
      },
      resolveOutputAddress,
      attachRowLookup(next: (id: RowLocalId) => Option<ModelRow>): void {
        lookup = next;
      },
    };
  }

  /** The `data:`-node key mapper for a row-keyed store bound to `binding`. */
  export function cellBump(binding: LedgerBinding): CellLedgerBump<RowLocalId> {
    return cellLedgerBump(binding);
  }
}
